// Deposit bag verification rules, ported from the Library cash page
// (public/js/cash-management.js depositStepHtml / bank-run guardrails) so the
// manager's check means the same thing in both apps.
//
// A bag is one Square drawer shift. Its expected contents are that shift's
// cash sales (cash_payment_cents), which is why a bag can be verified even
// when the closer never finished Station's closing count. The server function
// verify_drawer_deposit records who verified it and, once every CLOSED shift
// on the business day is verified, writes the day's deposit total.

export interface BagShift {
  id: string;
  device_id: string;
  state: string;
  cash_payment_cents?: number | null;
  cash_paid_in_cents?: number | null;
  /** Cash taken on later business days on a drawer left open (see splitHeldOverCash); excluded from this bag. */
  held_over_cents?: number | null;
  staff_counted_cents?: number | null;
  staff_counted_at?: string | null;
}

export interface BagCount {
  shift_id?: string | null;
  device_id: string;
  count_type: string;
  deposit_bag_number?: string | null;
  deposit_cents?: number | null;
}

export function bagExpectedCents(shift: BagShift): number {
  return Math.max(0, shift.cash_payment_cents ?? 0);
}

/** "12.34" / "$1,234.5" / ".50" / "12" -> cents. Null when it is not a money amount.
 *  Commas only as thousands separators, so "1,2,3" is refused rather than read as $123. */
export function parseDollarsToCents(input: string): number | null {
  const s = input.trim().replace(/^\$\s*/, "");
  if (!/^(\d{1,3}(,\d{3})+|\d+)?(\.\d{0,2})?$/.test(s) || !/\d/.test(s)) return null;
  return Math.round(parseFloat(s.replace(/,/g, "")) * 100);
}

export function normalizeBagNumber(raw: string | null | undefined): string {
  return (raw ?? "").trim().toUpperCase();
}

export type BagMatch = "match" | "mismatch" | "no-record" | "not-scanned";

/** Compare the bag number the verifier entered or scanned with the one the closer recorded. */
export function bagMatch(entered: string, recorded: string | null | undefined): BagMatch {
  const e = normalizeBagNumber(entered);
  const r = normalizeBagNumber(recorded);
  if (!e) return "not-scanned";
  if (!r) return "no-record";
  return e === r ? "match" : "mismatch";
}

export interface BagCheck {
  ok: boolean;
  error?: string;
  varianceCents: number | null;
}

/**
 * The guardrails on submitting a bag count:
 *  - the counted amount must be a money amount;
 *  - $0 against a bag that should hold cash is a typo or an empty bag, never a
 *    silent pass;
 *  - any variance, or a bag number that differs from the closer's, needs a note.
 */
export function checkBagVerification(args: {
  expectedCents: number;
  countedInput: string;
  note: string;
  match: BagMatch;
}): BagCheck {
  const counted = parseDollarsToCents(args.countedInput);
  if (counted == null) {
    return { ok: false, error: "Enter the amount counted in the bag.", varianceCents: null };
  }
  const variance = counted - args.expectedCents;
  const hasNote = args.note.trim().length > 0;
  if (counted === 0 && args.expectedCents > 0 && !hasNote) {
    return {
      ok: false,
      error: "A $0 count against a bag that should hold cash needs a note (empty or missing bag?).",
      varianceCents: variance,
    };
  }
  if (variance !== 0 && !hasNote) {
    return { ok: false, error: "Add a note explaining the variance.", varianceCents: variance };
  }
  if (args.match === "mismatch" && !hasNote) {
    return {
      ok: false,
      error: "The bag number does not match the closer's. Add a note before verifying.",
      varianceCents: variance,
    };
  }
  return { ok: true, varianceCents: variance };
}

/** The note saved with the verification. The bag number goes first so it is searchable. */
export function buildVerifyNote(bagNumber: string, match: BagMatch, note: string): string | undefined {
  const parts: string[] = [];
  const bag = normalizeBagNumber(bagNumber);
  if (bag) {
    const tag = match === "mismatch" ? " (does not match closer's bag)" : match === "no-record" ? " (no closer count)" : "";
    parts.push(`Bag #${bag}${tag}`);
  }
  if (note.trim()) parts.push(note.trim());
  return parts.length ? parts.join(" · ") : undefined;
}

/** The closer's recorded closing count for a shift: by shift id, else by register,
 *  but only when the register had a single shift that day; with two, a
 *  register match could hand one shift the other's bag. */
export function closingCountForShift<C extends BagCount>(shift: BagShift, counts: C[], dayShifts: BagShift[]): C | undefined {
  const closing = counts.filter((c) => c.count_type === "close" || c.count_type === "closing");
  const byId = closing.find((c) => c.shift_id && c.shift_id === shift.id);
  if (byId) return byId;
  if (dayShifts.filter((s) => s.device_id === shift.device_id).length > 1) return undefined;
  return closing.find((c) => c.device_id === shift.device_id);
}

/** Closed, unverified, no cash sales, no paid-in cash, and the closer did not bag any cash: safe to
 *  verify at $0 without a person. Paid-in cash or a closer-recorded deposit sends the shift to a normal card. */
export function isZeroBag(shift: BagShift, counts: BagCount[], dayShifts: BagShift[]): boolean {
  if (shift.state !== "CLOSED" || shift.staff_counted_at || bagExpectedCents(shift) !== 0) return false;
  if ((shift.cash_paid_in_cents ?? 0) > 0) return false;
  // A drawer left open for days needs a person to look at it, even if this day took no cash.
  if ((shift.held_over_cents ?? 0) > 0) return false;
  return !((closingCountForShift(shift, counts, dayShifts)?.deposit_cents ?? 0) > 0);
}

export interface DayBagStatus {
  closed: number;
  verified: number;
  pending: number;
  /** Still OPEN or ENDED in Square: cannot be verified until closed there. */
  notClosed: number;
}

export function dayBagStatus(shifts: BagShift[]): DayBagStatus {
  const closed = shifts.filter((s) => s.state === "CLOSED");
  const verified = closed.filter((s) => !!s.staff_counted_at).length;
  return {
    closed: closed.length,
    verified,
    pending: closed.length - verified,
    notClosed: shifts.length - closed.length,
  };
}

export interface RegisterCashSales {
  /** Sum of the register's CLOSED shifts' cash sales: what its deposit bag(s) should hold. */
  closedCents: number;
  /** CLOSED shifts that day; above 1 means more than one bag for this register. */
  closedShifts: number;
  /** Cash sales so far on a shift still open or ended-but-not-closed in Square (last sync), else null. */
  openCents: number | null;
}

/** A register's cash sales for the day, from Square drawer shifts. Same rule as the bag check. */
export function registerCashSales(shifts: BagShift[], deviceId: string): RegisterCashSales {
  const mine = shifts.filter((s) => s.device_id === deviceId);
  const closed = mine.filter((s) => s.state === "CLOSED");
  const open = mine.filter((s) => s.state !== "CLOSED");
  return {
    closedCents: closed.reduce((sum, s) => sum + bagExpectedCents(s), 0),
    closedShifts: closed.length,
    openCents: open.length ? open.reduce((sum, s) => sum + bagExpectedCents(s), 0) : null,
  };
}

/**
 * Shifts in the order the register cards above them are drawn (registerOrder =
 * the banks' device ids), then by when each drawer opened. Registers with no
 * card go last, alphabetically by device name.
 */
export function orderByRegister<S extends BagShift & { device_name?: string | null; opened_at?: string | null }>(
  shifts: S[],
  registerOrder: string[]
): S[] {
  const rank = (s: S) => {
    const i = registerOrder.indexOf(s.device_id);
    return i === -1 ? registerOrder.length : i;
  };
  return [...shifts].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      (rank(a) === registerOrder.length ? (a.device_name || "").localeCompare(b.device_name || "") : 0) ||
      (a.opened_at || "").localeCompare(b.opened_at || "")
  );
}

export interface CashEvent {
  shift_id: string;
  event_type: string;
  amount_cents: number | null;
  created_at: string;
}

export interface HeldOverDay {
  business_date: string;
  cents: number;
}

/**
 * A Square drawer left open for days (an "open" handheld picking up event
 * sales weeks later) carries every later cash payment in its total, and the
 * server files that total under the day the drawer OPENED. For the bag check,
 * only cash taken on the drawer's own business day belongs in that day's bag.
 *
 * Applies only to drawers open more than 24 hours, and only when the payment
 * events add up to the drawer's cash total (178 of 178 closed drawers since
 * June do); otherwise the drawer total is kept. Returns the adjusted shift
 * (cash_payment_cents = that day's cash, held_over_cents = the rest) and the
 * later days' amounts, or the shift unchanged with byDay = [].
 */
export function splitHeldOverCash<S extends BagShift & { opened_at?: string | null; closed_at?: string | null }>(
  shift: S,
  events: CashEvent[],
  businessDateOf: (iso: string) => string
): { shift: S; byDay: HeldOverDay[] } {
  const total = shift.cash_payment_cents ?? 0;
  if (!shift.opened_at || !shift.closed_at || total <= 0) return { shift, byDay: [] };
  const openMs = Date.parse(shift.closed_at) - Date.parse(shift.opened_at);
  if (!Number.isFinite(openMs) || openMs <= 24 * 3600 * 1000) return { shift, byDay: [] };
  const payments = events.filter((e) => e.shift_id === shift.id && e.event_type === "CASH_TENDER_PAYMENT");
  const sum = payments.reduce((acc, e) => acc + (e.amount_cents ?? 0), 0);
  if (sum !== total) return { shift, byDay: [] };
  const ownDay = businessDateOf(shift.opened_at);
  const perDay = new Map<string, number>();
  for (const e of payments) {
    const d = businessDateOf(e.created_at);
    perDay.set(d, (perDay.get(d) ?? 0) + (e.amount_cents ?? 0));
  }
  const ownCents = perDay.get(ownDay) ?? 0;
  const byDay = [...perDay.entries()]
    .filter(([d, c]) => d !== ownDay && c !== 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([business_date, cents]) => ({ business_date, cents }));
  if (byDay.length === 0) return { shift, byDay: [] };
  return { shift: { ...shift, cash_payment_cents: ownCents, held_over_cents: total - ownCents }, byDay };
}

export interface DrawerStaffShift {
  device_id: string;
  state: string;
  opened_at?: string | null;
  opening_staff_name?: string | null;
  closing_staff_name?: string | null;
}

/**
 * Who opened and who closed a register's Square drawer that day: the first
 * shift's opener and the last CLOSED shift's closer, so a register with two
 * drawer shifts names the day's first opener and last closer. Blank names
 * (Square leaves some empty) read as null.
 */
export function drawerStaff(shifts: DrawerStaffShift[], deviceId: string): { openedBy: string | null; closedBy: string | null } {
  const mine = shifts
    .filter((s) => s.device_id === deviceId)
    .sort((a, b) => (a.opened_at || "").localeCompare(b.opened_at || ""));
  const clean = (n: string | null | undefined) => (n && n.trim() ? n.trim() : null);
  const closed = mine.filter((s) => s.state === "CLOSED");
  return {
    openedBy: clean(mine[0]?.opening_staff_name),
    closedBy: clean(closed[closed.length - 1]?.closing_staff_name),
  };
}
